import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  uploadConnectorService,
  UploadProduct,
  UploadConnector,
} from '../services/uploadConnector';
import {
  amazonExportService,
  AmazonProductType,
  EnrichmentResult,
  EnrichedField,
  ValidationIssue,
  ValidationResult,
} from '../services/amazonExport';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  ShoppingCart,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Edit3,
  Sparkles,
  Eye,
  ScanBarcode,
  Brain,
  Wand2,
  Settings,
  FileSpreadsheet,
} from 'lucide-react';

type WizardStep = 'type-mapping' | 'enrichment' | 'review' | 'export';

const STEPS: { key: WizardStep; label: string; number: number }[] = [
  { key: 'type-mapping', label: 'Product Types', number: 1 },
  { key: 'enrichment', label: 'Enrichment', number: 2 },
  { key: 'review', label: 'Review & Edit', number: 3 },
  { key: 'export', label: 'Export', number: 4 },
];

// Source badge colors
const SOURCE_COLORS: Record<string, string> = {
  seller: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  vision: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  llm: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  rule: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  barcode: 'bg-green-500/10 text-green-600 border-green-500/20',
};

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  seller: <Edit3 className="h-3 w-3" />,
  vision: <Eye className="h-3 w-3" />,
  llm: <Brain className="h-3 w-3" />,
  rule: <Settings className="h-3 w-3" />,
  barcode: <ScanBarcode className="h-3 w-3" />,
};

export const AmazonExportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [currentStep, setCurrentStep] = useState<WizardStep>('type-mapping');
  const [connector, setConnector] = useState<UploadConnector | null>(null);
  const [products, setProducts] = useState<UploadProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Step 1: Type mapping
  const [productTypes, setProductTypes] = useState<AmazonProductType[]>([]);
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);

  // Step 2: Enrichment
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [jobProgress, setJobProgress] = useState({ total: 0, completed: 0, failed: 0 });
  const [isEnriching, setIsEnriching] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step 3: Review
  const [enrichmentResults, setEnrichmentResults] = useState<EnrichmentResult[]>([]);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [editTimers, setEditTimers] = useState<Record<string, ReturnType<typeof setTimeout>>>({});
  const [isValidating, setIsValidating] = useState<Record<string, boolean>>({});

  // Step 4: Export
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  // Load connector and products
  useEffect(() => {
    const productIdsParam = searchParams.get('products');
    if (!productIdsParam) {
      navigate('/upload-products');
      return;
    }

    const loadData = async () => {
      try {
        const conn = await uploadConnectorService.getDefaultConnector();
        setConnector(conn);

        const productIds = productIdsParam.split(',');
        // Fetch each product
        const productPromises = productIds.map((id) =>
          uploadConnectorService.getProduct(conn.id, id).catch(() => null)
        );
        const fetched = await Promise.all(productPromises);
        const validProducts = fetched.filter((p): p is UploadProduct => p !== null);

        if (validProducts.length === 0) {
          toast({ title: 'Error', description: 'No valid products found', variant: 'destructive' });
          navigate('/upload-products');
          return;
        }

        setProducts(validProducts);

        // Pre-fill type map from existing product_type
        const initialTypes: Record<string, string> = {};
        for (const p of validProducts) {
          if (p.product_type) {
            initialTypes[p.id] = p.product_type;
          }
        }
        setTypeMap(initialTypes);

        // If view=true, load existing enrichment and skip to review
        if (searchParams.get('view') === 'true') {
          const enrichmentPromises = validProducts.map((p) =>
            amazonExportService.getEnrichment(p.id).catch(() => null)
          );
          const enrichments = await Promise.all(enrichmentPromises);
          const validEnrichments = enrichments.filter(
            (e): e is EnrichmentResult => e !== null && e.enriched_fields != null
          );

          if (validEnrichments.length > 0) {
            setEnrichmentResults(validEnrichments);
            setExpandedProducts(new Set(validEnrichments.map((r) => r.upload_product_id)));
            setCurrentStep('review');
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        toast({ title: 'Error', description: 'Failed to load products', variant: 'destructive' });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [searchParams, navigate, toast]);

  // Load product types when entering step 1
  useEffect(() => {
    const loadTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await amazonExportService.getProductTypes();
        setProductTypes(Array.isArray(types) ? types : []);
      } catch (err) {
        console.error('Failed to load product types:', err);
        // Provide fallback types
        setProductTypes([
          { id: 'SHIRT', label: 'Shirt' },
          { id: 'DRESS', label: 'Dress' },
          { id: 'PANTS', label: 'Pants' },
          { id: 'SHOES', label: 'Shoes' },
          { id: 'BAG', label: 'Bag' },
          { id: 'JEWELRY', label: 'Jewelry' },
          { id: 'WATCH', label: 'Watch' },
          { id: 'PRODUCT', label: 'Generic Product' },
        ]);
      } finally {
        setIsLoadingTypes(false);
      }
    };
    loadTypes();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      // Cleanup edit timers
      Object.values(editTimers).forEach(clearTimeout);
    };
  }, [editTimers]);

  // ============================================
  // Step 1: Type Mapping
  // ============================================

  const handleTypeChange = (productId: string, type: string) => {
    setTypeMap((prev) => ({ ...prev, [productId]: type }));
  };

  const allTypesAssigned = products.every((p) => typeMap[p.id]);

  const handleStartEnrichment = async () => {
    setIsEnriching(true);
    setCurrentStep('enrichment');

    try {
      const result = await amazonExportService.enrichProducts(
        products.map((p) => p.id),
        typeMap
      );

      setJobId(result.jobId);
      setJobStatus(result.status);
      setJobProgress(result.progress);

      if (result.status === 'completed') {
        // Sync enrichment done — fetch results
        await loadResults(result.jobId);
        setCurrentStep('review');
      } else {
        // Start polling for async job
        startPolling(result.jobId);
      }
    } catch (err: any) {
      toast({
        title: 'Enrichment Failed',
        description: err.message || 'Failed to start enrichment',
        variant: 'destructive',
      });
      setCurrentStep('type-mapping');
    } finally {
      setIsEnriching(false);
    }
  };

  // ============================================
  // Step 2: Enrichment Polling
  // ============================================

  const startPolling = (jId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      try {
        const status = await amazonExportService.getJobStatus(jId);
        setJobStatus(status.status);
        setJobProgress(status.progress);

        if (status.status === 'completed' || status.status === 'failed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;

          if (status.status === 'completed') {
            await loadResults(jId);
            setCurrentStep('review');
          } else {
            toast({
              title: 'Enrichment Failed',
              description: 'Some products failed enrichment. You can review partial results.',
              variant: 'destructive',
            });
            await loadResults(jId);
            setCurrentStep('review');
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 15000);
  };

  const loadResults = async (jId: string) => {
    try {
      const data = await amazonExportService.getJobResults(jId);
      setEnrichmentResults(data.results || []);
      // Expand all by default
      setExpandedProducts(new Set(data.results.map((r) => r.upload_product_id)));
    } catch (err) {
      console.error('Failed to load results:', err);
    }
  };

  // ============================================
  // Step 3: Review & Edit
  // ============================================

  const getProductForResult = (result: EnrichmentResult) =>
    products.find((p) => p.id === result.upload_product_id);

  const toggleExpanded = (productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const handleFieldEdit = useCallback(
    (productId: string, fieldName: string, value: any) => {
      // Update local state immediately
      setEnrichmentResults((prev) =>
        prev.map((r) => {
          if (r.upload_product_id !== productId) return r;
          return { ...r, user_edits: { ...r.user_edits, [fieldName]: value } };
        })
      );

      // Debounce save (500ms)
      const timerKey = `${productId}:${fieldName}`;
      setEditTimers((prev) => {
        if (prev[timerKey]) clearTimeout(prev[timerKey]);
        const timer = setTimeout(async () => {
          try {
            await amazonExportService.saveEdits(productId, { [fieldName]: value });
          } catch (err) {
            console.error('Failed to save edit:', err);
          }
        }, 500);
        return { ...prev, [timerKey]: timer };
      });
    },
    []
  );

  const handleValidateProduct = useCallback(async (productId: string) => {
    setIsValidating((prev) => ({ ...prev, [productId]: true }));
    try {
      const result = await amazonExportService.validateProduct(productId);
      setEnrichmentResults((prev) =>
        prev.map((r) => {
          if (r.upload_product_id !== productId) return r;
          return { ...r, validation_result: result };
        })
      );
      toast({ title: 'Validation Complete' });
    } catch (err: any) {
      toast({
        title: 'Validation Failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setIsValidating((prev) => ({ ...prev, [productId]: false }));
    }
  }, [toast]);

  const handleValidateAll = async () => {
    for (const result of enrichmentResults) {
      if (result.status !== 'failed') {
        await handleValidateProduct(result.upload_product_id);
      }
    }
  };

  const passingCount = enrichmentResults.filter(
    (r) => r.validation_result?.valid
  ).length;
  const totalEnriched = enrichmentResults.filter((r) => r.status !== 'failed').length;

  // ============================================
  // Step 4: Export
  // ============================================

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const productIds = enrichmentResults
        .filter((r) => r.status !== 'failed')
        .map((r) => r.upload_product_id);

      await amazonExportService.exportToXlsx(productIds);
      setExportComplete(true);
      toast({ title: 'Export Successful', description: 'Your xlsx file has been downloaded.' });
      setCurrentStep('export');
    } catch (err: any) {
      toast({
        title: 'Export Failed',
        description: err.message || 'Failed to generate export',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // ============================================
  // Rendering
  // ============================================

  const getFieldValue = (result: EnrichmentResult, fieldName: string): any => {
    if (result.user_edits && fieldName in result.user_edits) {
      return result.user_edits[fieldName];
    }
    if (result.enriched_fields && result.enriched_fields[fieldName]) {
      return result.enriched_fields[fieldName].value;
    }
    return '';
  };

  const isFieldEdited = (result: EnrichmentResult, fieldName: string): boolean => {
    return result.user_edits != null && fieldName in result.user_edits;
  };

  const getFieldValidationIssues = (
    result: EnrichmentResult,
    fieldName: string
  ): ValidationIssue[] => {
    if (!result.validation_result?.issues) return [];
    return result.validation_result.issues.filter((i) => i.field === fieldName);
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/upload-products')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-amber-600" />
              Export for Amazon
            </h1>
            <p className="text-sm text-muted-foreground">
              {products.length} product{products.length !== 1 ? 's' : ''} selected
            </p>
          </div>
        </div>
      </div>

      {/* Apparel-only notice */}
      <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-sm">
        <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-700">Apparel products only</p>
          <p className="text-muted-foreground mt-0.5">
            The Amazon export service currently supports apparel product types only (shirts, dresses, pants, shoes, etc.). Support for additional categories is coming soon.
          </p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((step, idx) => {
          const isActive = step.key === currentStep;
          const stepIndex = STEPS.findIndex((s) => s.key === currentStep);
          const isPast = idx < stepIndex;

          return (
            <React.Fragment key={step.key}>
              {idx > 0 && (
                <div className={`flex-1 h-0.5 ${isPast ? 'bg-primary' : 'bg-muted'}`} />
              )}
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : isPast
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {isPast ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                    {step.number}
                  </span>
                )}
                <span className="hidden sm:inline">{step.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      {currentStep === 'type-mapping' && (
        <StepTypeMapping
          products={products}
          productTypes={productTypes}
          typeMap={typeMap}
          isLoadingTypes={isLoadingTypes}
          onTypeChange={handleTypeChange}
          allTypesAssigned={allTypesAssigned}
          onStartEnrichment={handleStartEnrichment}
          isEnriching={isEnriching}
        />
      )}

      {currentStep === 'enrichment' && (
        <StepEnrichment
          progress={jobProgress}
          status={jobStatus}
          products={products}
        />
      )}

      {currentStep === 'review' && (
        <StepReview
          products={products}
          enrichmentResults={enrichmentResults}
          expandedProducts={expandedProducts}
          toggleExpanded={toggleExpanded}
          getProductForResult={getProductForResult}
          getFieldValue={getFieldValue}
          isFieldEdited={isFieldEdited}
          getFieldValidationIssues={getFieldValidationIssues}
          getConfidenceColor={getConfidenceColor}
          onFieldEdit={handleFieldEdit}
          onValidateProduct={handleValidateProduct}
          onValidateAll={handleValidateAll}
          isValidating={isValidating}
          passingCount={passingCount}
          totalEnriched={totalEnriched}
          onExport={handleExport}
          isExporting={isExporting}
        />
      )}

      {currentStep === 'export' && (
        <StepExport
          exportComplete={exportComplete}
          onBack={() => navigate('/upload-products')}
          onReExport={handleExport}
          isExporting={isExporting}
        />
      )}
    </div>
  );
};

// ============================================
// Step 1: Type Mapping
// ============================================

const StepTypeMapping: React.FC<{
  products: UploadProduct[];
  productTypes: AmazonProductType[];
  typeMap: Record<string, string>;
  isLoadingTypes: boolean;
  onTypeChange: (productId: string, type: string) => void;
  allTypesAssigned: boolean;
  onStartEnrichment: () => void;
  isEnriching: boolean;
}> = ({
  products,
  productTypes,
  typeMap,
  isLoadingTypes,
  onTypeChange,
  allTypesAssigned,
  onStartEnrichment,
  isEnriching,
}) => (
  <div className="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle>Map Amazon Product Types</CardTitle>
        <CardDescription>
          Select the Amazon product type for each product. This determines which fields the AI will enrich.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoadingTypes ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading product types...
          </div>
        ) : (
          products.map((product) => (
            <div
              key={product.id}
              className="flex items-center gap-4 p-4 rounded-lg border bg-card"
            >
              {/* Product thumbnail */}
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted shrink-0">
                {product.cover_image ? (
                  <img
                    src={product.cover_image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                    No img
                  </div>
                )}
              </div>

              {/* Product info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {product.name_en || product.name_ar || 'Untitled'}
                </p>
                {product.barcode && (
                  <p className="text-xs text-muted-foreground font-mono">{product.barcode}</p>
                )}
              </div>

              {/* Type selector */}
              <Select
                value={typeMap[product.id] || ''}
                onValueChange={(value) => onTypeChange(product.id, value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {productTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))
        )}
      </CardContent>
    </Card>

    <div className="flex justify-end">
      <Button
        onClick={onStartEnrichment}
        disabled={!allTypesAssigned || isEnriching}
        className="gap-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
      >
        {isEnriching ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        Start Enrichment
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

// ============================================
// Step 2: Enrichment Progress
// ============================================

const StepEnrichment: React.FC<{
  progress: { total: number; completed: number; failed: number };
  status: string;
  products: UploadProduct[];
}> = ({ progress, status, products }) => {
  const percentage =
    progress.total > 0
      ? Math.round(((progress.completed + progress.failed) / progress.total) * 100)
      : 0;

  return (
    <Card>
      <CardContent className="py-12">
        <div className="max-w-md mx-auto text-center space-y-6">
          <div className="relative">
            <Wand2 className="h-12 w-12 text-amber-500 mx-auto animate-pulse" />
          </div>

          <div>
            <h2 className="text-xl font-semibold">
              {status === 'processing' ? 'AI Enrichment in Progress' : 'Starting Enrichment...'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {products.length === 1
                ? 'Processing your product with AI...'
                : `Processing ${progress.total} products with AI...`}
            </p>
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>
                {progress.completed} of {progress.total} completed
              </span>
              <span>{percentage}%</span>
            </div>
            {progress.failed > 0 && (
              <p className="text-sm text-destructive">
                {progress.failed} failed
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Step 3: Review & Edit
// ============================================

const StepReview: React.FC<{
  products: UploadProduct[];
  enrichmentResults: EnrichmentResult[];
  expandedProducts: Set<string>;
  toggleExpanded: (id: string) => void;
  getProductForResult: (r: EnrichmentResult) => UploadProduct | undefined;
  getFieldValue: (r: EnrichmentResult, field: string) => any;
  isFieldEdited: (r: EnrichmentResult, field: string) => boolean;
  getFieldValidationIssues: (r: EnrichmentResult, field: string) => ValidationIssue[];
  getConfidenceColor: (c: number) => string;
  onFieldEdit: (productId: string, field: string, value: any) => void;
  onValidateProduct: (productId: string) => void;
  onValidateAll: () => void;
  isValidating: Record<string, boolean>;
  passingCount: number;
  totalEnriched: number;
  onExport: () => void;
  isExporting: boolean;
}> = ({
  enrichmentResults,
  expandedProducts,
  toggleExpanded,
  getProductForResult,
  getFieldValue,
  isFieldEdited,
  getFieldValidationIssues,
  getConfidenceColor,
  onFieldEdit,
  onValidateProduct,
  onValidateAll,
  isValidating,
  passingCount,
  totalEnriched,
  onExport,
  isExporting,
}) => (
  <div className="space-y-4">
    {/* Top bar */}
    <Card>
      <CardContent className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">
            {passingCount} of {totalEnriched} passing validation
          </span>
          <Button variant="outline" size="sm" onClick={onValidateAll} className="gap-1">
            <RefreshCw className="h-3 w-3" />
            Re-validate All
          </Button>
        </div>
        <Button
          onClick={onExport}
          disabled={isExporting}
          className="gap-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-4 w-4" />
          )}
          Export to XLSX
        </Button>
      </CardContent>
    </Card>

    {/* Per-product cards */}
    {enrichmentResults.map((result) => {
      const product = getProductForResult(result);
      const isExpanded = expandedProducts.has(result.upload_product_id);
      const fieldNames = result.enriched_fields ? Object.keys(result.enriched_fields) : [];
      const validation = result.validation_result;

      return (
        <Card key={result.id} className={result.status === 'failed' ? 'border-destructive/50' : ''}>
          <CardContent className="p-0">
            {/* Collapsed header */}
            <button
              onClick={() => toggleExpanded(result.upload_product_id)}
              className="w-full p-4 flex items-center gap-4 text-left hover:bg-muted/30 transition-colors"
            >
              {/* Product thumbnail */}
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted shrink-0">
                {product?.cover_image ? (
                  <img src={product.cover_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                    ?
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {product?.name_en || product?.name_ar || 'Product'}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Type: {result.amazon_product_type}</span>
                  {product?.size && (
                    <>
                      <span className="text-muted-foreground/40">|</span>
                      <span>Size: {product.size}</span>
                    </>
                  )}
                  {product?.brand && (
                    <>
                      <span className="text-muted-foreground/40">|</span>
                      <span>Brand: {product.brand}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Status badges */}
              <div className="flex items-center gap-2 shrink-0">
                {result.status === 'failed' ? (
                  <Badge variant="destructive">Failed</Badge>
                ) : (
                  <>
                    {validation && (
                      <div className="flex items-center gap-1">
                        {(validation.summary?.error_count ?? validation.summary?.errors ?? 0) > 0 && (
                          <Badge variant="destructive" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            {validation.summary?.error_count ?? validation.summary?.errors ?? 0}
                          </Badge>
                        )}
                        {(validation.summary?.warning_count ?? validation.summary?.warnings ?? 0) > 0 && (
                          <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-500/30">
                            <AlertTriangle className="h-3 w-3" />
                            {validation.summary?.warning_count ?? validation.summary?.warnings ?? 0}
                          </Badge>
                        )}
                        {validation.valid && (
                          <Badge className="gap-1 bg-green-500/10 text-green-600 border-green-500/30">
                            <CheckCircle2 className="h-3 w-3" />
                            Valid
                          </Badge>
                        )}
                      </div>
                    )}
                    {validation?.completeness && (
                      <span className="text-xs text-muted-foreground">
                        {validation.completeness.overall ?? validation.completeness.percentage ?? 0}%
                      </span>
                    )}
                  </>
                )}
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </button>

            {/* Expanded field editor */}
            {isExpanded && result.status !== 'failed' && (
              <div className="border-t px-4 pb-4">
                {/* Stage errors */}
                {result.stage_errors && Object.values(result.stage_errors).some(Boolean) && (
                  <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-sm font-medium text-yellow-700 flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      Processing Warnings
                    </p>
                    {Object.entries(result.stage_errors)
                      .filter(([, v]) => v)
                      .map(([stage, msg]) => (
                        <p key={stage} className="text-xs text-yellow-600 mt-1">
                          {stage}: {msg}
                        </p>
                      ))}
                  </div>
                )}

                {/* Completeness bar */}
                {validation?.completeness && (() => {
                  const filled = validation.completeness.required?.filled ?? validation.completeness.filled ?? 0;
                  const total = validation.completeness.required?.total ?? validation.completeness.total ?? 0;
                  const pct = validation.completeness.overall ?? validation.completeness.percentage ?? (total > 0 ? Math.round((filled / total) * 100) : 0);
                  return (
                    <div className="mt-3 space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Completeness</span>
                        <span>
                          {filled}/{total} fields ({pct}%)
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Fields grid */}
                <div className="mt-4 space-y-3">
                  {fieldNames.map((fieldName) => {
                    const enrichedField = result.enriched_fields[fieldName];
                    const value = getFieldValue(result, fieldName);
                    const edited = isFieldEdited(result, fieldName);
                    const issues = getFieldValidationIssues(result, fieldName);
                    const hasError = issues.some((i) => i.severity === 'error');
                    const isArray = Array.isArray(enrichedField?.value);

                    return (
                      <div key={fieldName} className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm font-medium">{fieldName}</Label>
                          {/* Source badge */}
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                              SOURCE_COLORS[enrichedField?.source] || SOURCE_COLORS.rule
                            }`}
                          >
                            {SOURCE_ICONS[enrichedField?.source]}
                            {enrichedField?.source}
                          </span>
                          {/* Confidence */}
                          {enrichedField?.confidence != null && (
                            <span
                              className={`text-[10px] font-mono ${getConfidenceColor(
                                enrichedField.confidence
                              )}`}
                            >
                              {Math.round(enrichedField.confidence * 100)}%
                            </span>
                          )}
                          {/* Edited indicator */}
                          {edited && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              Edited
                            </Badge>
                          )}
                        </div>

                        {/* Input */}
                        {isArray ? (
                          <div className="space-y-1">
                            {(Array.isArray(value) ? value : [value]).map(
                              (item: string, idx: number) => (
                                <Input
                                  key={idx}
                                  value={item || ''}
                                  onChange={(e) => {
                                    const arr = Array.isArray(value)
                                      ? [...value]
                                      : [value];
                                    arr[idx] = e.target.value;
                                    onFieldEdit(result.upload_product_id, fieldName, arr);
                                  }}
                                  className={`text-sm ${hasError ? 'border-destructive' : ''}`}
                                />
                              )
                            )}
                          </div>
                        ) : issues.some((i) => i.suggested_values && i.suggested_values.length > 0) ? (
                          <Select
                            value={value || ''}
                            onValueChange={(v) =>
                              onFieldEdit(result.upload_product_id, fieldName, v)
                            }
                          >
                            <SelectTrigger
                              className={`text-sm ${hasError ? 'border-destructive' : ''}`}
                            >
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {value && (
                                <SelectItem value={value}>{value}</SelectItem>
                              )}
                              {issues
                                .flatMap((i) => i.suggested_values || [])
                                .filter((sv) => sv !== value)
                                .map((sv) => (
                                  <SelectItem key={sv} value={sv}>
                                    {sv}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={value || ''}
                            onChange={(e) =>
                              onFieldEdit(
                                result.upload_product_id,
                                fieldName,
                                e.target.value
                              )
                            }
                            className={`text-sm ${hasError ? 'border-destructive' : ''}`}
                          />
                        )}

                        {/* Validation messages */}
                        {issues.map((issue, idx) => (
                          <p
                            key={idx}
                            className={`text-xs flex items-center gap-1 ${
                              issue.severity === 'error'
                                ? 'text-destructive'
                                : issue.severity === 'warning'
                                ? 'text-yellow-600'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {issue.severity === 'error' ? (
                              <XCircle className="h-3 w-3" />
                            ) : issue.severity === 'warning' ? (
                              <AlertTriangle className="h-3 w-3" />
                            ) : (
                              <Info className="h-3 w-3" />
                            )}
                            {issue.message}
                          </p>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* Product actions */}
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onValidateProduct(result.upload_product_id)}
                    disabled={isValidating[result.upload_product_id]}
                    className="gap-1"
                  >
                    {isValidating[result.upload_product_id] ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    Re-validate
                  </Button>
                </div>
              </div>
            )}

            {/* Expanded but failed */}
            {isExpanded && result.status === 'failed' && (
              <div className="border-t px-4 py-4">
                <p className="text-sm text-destructive">{result.error_message || 'Enrichment failed for this product.'}</p>
              </div>
            )}
          </CardContent>
        </Card>
      );
    })}
  </div>
);

// ============================================
// Step 4: Export Complete
// ============================================

const StepExport: React.FC<{
  exportComplete: boolean;
  onBack: () => void;
  onReExport: () => void;
  isExporting: boolean;
}> = ({ exportComplete, onBack, onReExport, isExporting }) => (
  <Card>
    <CardContent className="py-16">
      <div className="max-w-md mx-auto text-center space-y-6">
        {exportComplete ? (
          <>
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <div>
              <h2 className="text-xl font-semibold">Export Complete!</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Your Amazon listing xlsx file has been downloaded.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Exported at {new Date().toLocaleString()}
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={onBack}>
                Back to Products
              </Button>
              <Button onClick={onReExport} disabled={isExporting} className="gap-2">
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Re-export
              </Button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Generating export file...</p>
          </>
        )}
      </div>
    </CardContent>
  </Card>
);
